import { UNKNOWN_PARENT, UNNAMED_SPINCO, type SituationSummary } from "@mosaic/shared";
import { denseCompanyLabel } from "./format.js";

/**
 * The three columns Table mode can be ordered by, and the two directions.
 *
 * Declared as values rather than as a bare union because `parseSort` checks a stored key
 * against them at runtime, and a second list would drift with neither copy failing.
 */
export const SORT_KEYS = ["spinco", "parent", "days"] as const;
/** The two directions a Table mode column can be sorted in. */
export const SORT_DIRS = ["asc", "desc"] as const;

/** A column Table mode can be ordered by. */
export type SortKey = (typeof SORT_KEYS)[number];
/** A sort direction: `asc` or `desc`. */
export type SortDir = (typeof SORT_DIRS)[number];
/** An ordering of Table mode: the column and the direction. */
export interface Sort {
  key: SortKey;
  dir: SortDir;
}

/** Where the reader's last ordering lives, namespaced like `mosaic.theme`. */
const STORAGE_KEY = "mosaic.tableSort";

type Row = Pick<SituationSummary, "spinco" | "parent" | "daysToDistribution">;

/**
 * A row's value on the sorted column, or null when it has none.
 *
 * A null row is unranked and sits after every ranked row in both directions.
 * `daysToDistribution` is null for a separation with no distribution date, and sorting it
 * as a small number would claim a date the filings never made; a placeholder name sorted
 * under "(" would file every unnamed SpinCo at the top as though that were its place.
 * Reversing the direction does not promote an absence, so the top of the list is always
 * the extreme the reader asked for.
 *
 * @internal
 */
function rank(row: Row, key: SortKey): string | number | null {
  if (key === "days") return row.daysToDistribution;
  const side = key === "spinco" ? row.spinco : row.parent;
  const placeholder = key === "spinco" ? UNNAMED_SPINCO : UNKNOWN_PARENT;
  if (side.name === placeholder) return null;
  // Sorted by the label the table shows, since that is what the reader is ordering.
  return denseCompanyLabel(side);
}

/**
 * Order a table page. Returns a new array; the input is untouched.
 *
 * `null` sort means the server's own order, which is what the page opens on.
 *
 * Stable: ties keep the order they arrived in, made explicit by the index tiebreak, so two
 * situations sharing a distribution date do not swap because an unrelated row changed.
 */
export function sortSituations<T extends Row>(rows: readonly T[], sort: Sort | null): T[] {
  const out = rows.slice();
  if (!sort) return out;

  const direction = sort.dir === "asc" ? 1 : -1;
  const index = new Map(rows.map((r, i) => [r, i]));

  return out.sort((a, b) => {
    const ra = rank(a, sort.key);
    const rb = rank(b, sort.key);

    // Unranked rows are last whichever way the arrow points, so this comparison is made
    // BEFORE the direction is applied rather than being flipped with everything else.
    if (ra === null && rb === null) return (index.get(a) ?? 0) - (index.get(b) ?? 0);
    if (ra === null) return 1;
    if (rb === null) return -1;

    let cmp: number;
    if (typeof ra === "number" && typeof rb === "number") {
      cmp = ra - rb;
    } else {
      // `localeCompare` rather than `<`: it puts "Ålesund" beside "A" instead of after
      // "Z", and is case-insensitive in the way a reader expects a name column to be.
      cmp = String(ra).localeCompare(String(rb), undefined, { sensitivity: "base" });
    }
    if (cmp !== 0) return cmp * direction;
    return (index.get(a) ?? 0) - (index.get(b) ?? 0);
  });
}

/**
 * What pressing a header does: a new column starts ascending, the current one flips.
 *
 * Two states, not three: a press that cleared the sort would be a third position on a
 * control drawn as an up/down toggle, and would look like a press that did nothing.
 */
export function nextSort(current: Sort | null, key: SortKey): Sort {
  if (current?.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  return { key, dir: "asc" };
}

/**
 * Read a stored sort, refusing anything this build cannot order by.
 *
 * `rank()` sends any unrecognised key to the parent column, so an unchecked key would
 * order the table by a column whose header is not lit. Storage outlives a deploy, so a key
 * removed in a later version can still be stored. An unreadable value is the same answer as
 * no value: the table opens in the server's order.
 */
export function parseSort(raw: string | null): Sort | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const { key, dir } = parsed as { key?: unknown; dir?: unknown };
  if (!SORT_KEYS.includes(key as SortKey)) return null;
  if (!SORT_DIRS.includes(dir as SortDir)) return null;
  return { key: key as SortKey, dir: dir as SortDir };
}

/**
 * The stored sort, or null. `localStorage` throws on access in a private window or behind
 * blocked site data, so the whole expression is guarded rather than the parse alone.
 */
export function readStoredSort(): Sort | null {
  try {
    return parseSort(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Store the sort. Takes a `Sort` and never null, because `nextSort` never produces one; a
 * failure to store is ignored.
 */
export function writeStoredSort(sort: Sort): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // Persisting is a convenience; failing to do so must not break the header.
  }
}
